<template>
  <Teleport to="body">
    <Transition name="bubble">
      <div v-if="modelValue" class="bubble-overlay">
        <div
          ref="bubbleRef"
          class="bubble-content"
          :style="bubbleStyle.box"
          :class="`tail-${tail}`"
        >
          <div class="bubble-tail" :style="bubbleStyle.tail"></div>
          <slot></slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch, nextTick, onUnmounted } from 'vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  anchor:     { type: Object,  default: null  },
  tail:       { type: String,  default: 'left' },
})

const emits = defineEmits(['update:modelValue'])

const OFFSET        = 16
const TAIL_SIZE     = 12
const SCREEN_MARGIN = 8
const BORDER_RADIUS = 12

const bubbleRef    = ref(null)
const bubbleHeight = ref(0)
const bubbleWidth  = ref(0)

// After the bubble appears, measure its real dimensions
watch(() => props.modelValue, async (val) => {
  if (val) {
    window.addEventListener('scroll', onScroll, { passive: true, capture: true })
    await nextTick()
    if (bubbleRef.value) {
      bubbleHeight.value = bubbleRef.value.offsetHeight
      bubbleWidth.value  = bubbleRef.value.offsetWidth
    }
  } else {
    window.removeEventListener('scroll', onScroll, { capture: true })
    bubbleHeight.value = 0
    bubbleWidth.value  = 0
  }
})

onUnmounted(() => window.removeEventListener('scroll', onScroll, { capture: true }))

function close()    { emits('update:modelValue', false) }
function onScroll() { close() }

const bubbleStyle = computed(() => {
  if (!props.anchor) return { box: {}, tail: {} }

  const { top, left, width, height } = props.anchor
  const vw = window.innerWidth
  const vh = window.innerHeight

  const anchorCenterX = left + width  / 2
  const anchorCenterY = top  + height / 2

  // If the anchor itself has scrolled off screen, don't show
  const anchorVisibleY = top >= -height  && top <= vh + height
  const anchorVisibleX = left >= -width  && left <= vw + width
  if (!anchorVisibleY || !anchorVisibleX) return { box: {}, tail: {} }

  if (props.tail === 'left' || props.tail === 'right') {
    const bh = bubbleHeight.value || 10 // start small, re-renders after measure

    const idealTop    = anchorCenterY - bh / 2
    const clampedTop  = Math.min(Math.max(idealTop, SCREEN_MARGIN), vh - bh - SCREEN_MARGIN)

    let tailTop = anchorCenterY - clampedTop
    tailTop = Math.max(BORDER_RADIUS + TAIL_SIZE, Math.min(tailTop, bh - BORDER_RADIUS - TAIL_SIZE))

    const boxLeft  = props.tail === 'left'  ? left + width + OFFSET : 'auto'
    const boxRight = props.tail === 'right' ? vw - left + OFFSET    : 'auto'

    return {
      box: {
        top:       `${clampedTop}px`,
        left:      boxLeft !== 'auto' ? `${boxLeft}px` : 'auto',
        right:     boxRight !== 'auto' ? `${boxRight}px` : 'auto',
        transform: 'none',
      },
      tail: {
        top:       `${tailTop}px`,
        transform: 'translateY(-50%)',
      }
    }
  }

  if (props.tail === 'bottom' || props.tail === 'top') {
    const bw = bubbleWidth.value || 10

    const idealLeft   = anchorCenterX - bw / 2
    const clampedLeft = Math.min(Math.max(idealLeft, SCREEN_MARGIN), vw - bw - SCREEN_MARGIN)

    let tailLeft = anchorCenterX - clampedLeft
    tailLeft = Math.max(BORDER_RADIUS + TAIL_SIZE, Math.min(tailLeft, bw - BORDER_RADIUS - TAIL_SIZE))

    const boxTop = props.tail === 'bottom'
      ? top - OFFSET
      : top + height + OFFSET

    return {
      box: {
        left:      `${clampedLeft}px`,
        top:       `${boxTop}px`,
        transform: props.tail === 'bottom' ? 'translateY(-100%)' : 'none',
      },
      tail: {
        left:      `${tailLeft}px`,
        transform: 'translateX(-50%)',
      }
    }
  }

  return { box: {}, tail: {} }
})
</script>

<style scoped lang="scss">
$tail-size: 12px;

.bubble-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
}

.bubble-content {
  position: absolute;
  background: #fff;
  border-radius: 12px;
  padding: 1rem;
  min-width: 200px;
  max-width: 800px;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 2px 6px rgba(0, 0, 0, 0.08);
  pointer-events: auto;
}

.bubble-tail {
  position: absolute;
  width: 0;
  height: 0;
  border: $tail-size solid transparent;

  .tail-left & {
    right: 100%;
    border-right-color: #fff;
  }

  .tail-right & {
    left: 100%;
    border-left-color: #fff;
  }

  .tail-bottom & {
    top: 100%;
    border-top-color: #fff;
  }

  .tail-top & {
    bottom: 100%;
    border-bottom-color: #fff;
  }
}

.bubble-enter-active .bubble-content,
.bubble-leave-active .bubble-content {
  transition: opacity 0.15s ease;
}

.bubble-enter-from .bubble-content,
.bubble-leave-to .bubble-content {
  opacity: 0;
}
</style>